import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dealerShiftsApi, dealerHubApi, usersApi, gamesApi, notificationsApi } from '../services/api';
import { requestNotificationPermission } from '../utils/pwa';
import { AnimatedNumber, Avatar, Skeleton, ChipSpinner, Sparkline, ContributionHeatmap, fireConfetti, computeStreak, computeLevel, computeAchievements, getGreeting } from './DealerHubVisuals';
import './DealerHub.css';

function DealerHub({ username, userId, userLocation, onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('shifts');

  // Data state
  const [shifts, setShifts] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [swaps, setSwaps] = useState([]);
  const [allDealers, setAllDealers] = useState([]);
  const [openShifts, setOpenShifts] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [threads, setThreads] = useState([]);
  const [stats, setStats] = useState(null);
  const [todayGames, setTodayGames] = useState([]);
  const [selfCheckinModal, setSelfCheckinModal] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(null);
  const [error, setError] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('dh-dark') !== 'false');

  // Modals
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ sms_shift_reminders: true, sms_shortage_alerts: false, push_enabled: false });
  // Password change
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdMsg, setPwdMsg] = useState('');
  const [swapModal, setSwapModal] = useState(null);
  const [swapTarget, setSwapTarget] = useState('');
  const [swapNote, setSwapNote] = useState('');
  const [blockDateModal, setBlockDateModal] = useState(false);
  const [blockDate, setBlockDate] = useState('');
  const [blockNote, setBlockNote] = useState('');
  const [tipSplitModal, setTipSplitModal] = useState(false);
  const [tipAmount, setTipAmount] = useState('');
  const [tipDealers, setTipDealers] = useState(2);
  const [endShiftModal, setEndShiftModal] = useState(null);
  const [esPlayers, setEsPlayers] = useState('');
  const [esCash, setEsCash] = useState('');
  const [esTips, setEsTips] = useState('');
  const [esNotes, setEsNotes] = useState('');
  const [activeThread, setActiveThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadDraft, setThreadDraft] = useState('');
  const [incidentModal, setIncidentModal] = useState(false);
  const [incType, setIncType] = useState('player');
  const [incDesc, setIncDesc] = useState('');
  const [incSeverity, setIncSeverity] = useState('low');

  // Pull-to-refresh
  const [refreshing, setRefreshing] = useState(false);
  const touchStart = useRef(0);

  // Apply dark mode
  useEffect(() => {
    document.body.style.background = darkMode ? '#0d1929' : '#f4f6f8';
    localStorage.setItem('dh-dark', String(darkMode));
  }, [darkMode]);

  // Online/offline detection
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [shiftsRes, earningsRes, messagesRes, swapsRes, dealersRes, openRes, availRes, threadsRes, statsRes] = await Promise.all([
        dealerShiftsApi.getShifts(userId).catch(() => ({ data: { shifts: [] } })),
        dealerShiftsApi.getEarnings(userId).catch(() => ({ data: { tonightEarnings: 0, totalEarnings: 0, totalShifts: 0, history: [] } })),
        dealerShiftsApi.getMessages(userId).catch(() => ({ data: { messages: [], unreadCount: 0 } })),
        dealerShiftsApi.getSwaps(userId).catch(() => ({ data: { swaps: [] } })),
        usersApi.getDealers().catch(() => ({ data: { dealers: [] } })),
        dealerHubApi.listOpenShifts().catch(() => ({ data: { openShifts: [] } })),
        dealerShiftsApi.getAvailability(userId).catch(() => ({ data: { availability: [] } })),
        dealerHubApi.getThreads(userId).catch(() => ({ data: { threads: [] } })),
        dealerHubApi.getStats(userId).catch(() => ({ data: { stats: {} } })),
      ]);
      setShifts(shiftsRes.data?.shifts || []);
      setEarnings(earningsRes.data || null);
      setMessages(messagesRes.data?.messages || []);
      setUnreadCount(messagesRes.data?.unreadCount || 0);
      setSwaps(swapsRes.data?.swaps || []);
      setAllDealers((dealersRes.data?.dealers || []).filter(d => d.id !== userId));
      setOpenShifts(openRes.data?.openShifts || []);
      setAvailability(availRes.data?.availability || []);
      setThreads(threadsRes.data?.threads || []);
      setStats(statsRes.data?.stats || null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); setRefreshing(false); }
  }, [userId]);

  const loadPrefs = async () => {
    try { const r = await dealerShiftsApi.getPrefs(userId); setPrefs(r.data?.prefs || prefs); } catch {}
  };

  useEffect(() => { if (userId) { fetchAll(); loadPrefs(); } }, [userId, fetchAll]);

  // Auto-refresh every 60s
  useEffect(() => {
    if (!userId) return;
    const i = setInterval(fetchAll, 60000);
    return () => clearInterval(i);
  }, [userId, fetchAll]);

  // ===== Actions =====
  const handleConfirm = async (gameId, status) => {
    try { await dealerShiftsApi.confirm(userId, gameId, status); fetchAll(); }
    catch (e) { setError(e.message); }
  };

  const handleCheckIn = async (gameId) => {
    setCheckingIn(gameId); setError(null);
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000, enableHighAccuracy: true })
      );
      const res = await dealerShiftsApi.checkIn(userId, gameId, pos.coords.latitude, pos.coords.longitude);
      if (res.success !== false) {
        fireConfetti();
        if (navigator.vibrate) navigator.vibrate([100, 50, 200]);
        fetchAll();
      } else setError(res.message || 'Check-in failed');
    } catch (e) { setError(e.message || 'Check-in failed - location required'); }
    finally { setCheckingIn(null); }
  };

  const handleMarkRead = async (id) => { await dealerShiftsApi.markRead(userId, id); fetchAll(); };

  // Self check-in: load today's games + assign self + check-in
  const openSelfCheckin = async () => {
    setSelfCheckinModal(true);
    try {
      const r = await gamesApi.getAll();
      const all = r.data?.games || r.games || [];
      const todayStart = new Date(); todayStart.setHours(0,0,0,0);
      const todayEnd = new Date(); todayEnd.setHours(23,59,59,999);
      setTodayGames(all
        .filter(g => {
          const d = new Date(g.startTime);
          return d >= todayStart && d <= todayEnd && g.status !== 'completed';
        })
        .sort((a,b) => new Date(a.startTime) - new Date(b.startTime))
      );
    } catch (e) { setError(e.message); }
  };

  const handleSelfCheckin = async (game) => {
    setCheckingIn(game.id); setError(null);
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000, enableHighAccuracy: true })
      );
      // Assign self as dealer first
      await gamesApi.assignDealerToTable(game.id, userId, 1, { recurring: false });
      // Then check in
      const res = await dealerShiftsApi.checkIn(userId, game.id, pos.coords.latitude, pos.coords.longitude);
      if (res.success !== false) {
        fireConfetti();
        if (navigator.vibrate) navigator.vibrate([100, 50, 200]);
        setSelfCheckinModal(false);
        fetchAll();
      } else setError(res.message || 'Check-in failed (must be within 1 mile of venue)');
    } catch (e) { setError(e.message || 'Check-in failed'); }
    finally { setCheckingIn(null); }
  };

  const handleRequestSwap = async () => {
    if (!swapModal || !swapTarget) return;
    try {
      await dealerShiftsApi.requestSwap(userId, swapModal.shift.gameId, swapTarget, swapNote);
      setSwapModal(null); setSwapTarget(''); setSwapNote(''); fetchAll();
    } catch (e) { setError(e.message); }
  };

  const handleRespondSwap = async (swapId, status) => {
    try { await dealerShiftsApi.respondSwap(userId, swapId, status); fetchAll(); }
    catch (e) { setError(e.message); }
  };

  const handleClaimOpen = async (id) => {
    try { await dealerHubApi.claimOpenShift(id, userId); fetchAll(); }
    catch (e) { setError(e.message); }
  };

  const handleBlockDate = async () => {
    if (!blockDate) return;
    try {
      await dealerHubApi.blockDate(userId, blockDate, blockNote);
      setBlockDateModal(false); setBlockDate(''); setBlockNote(''); fetchAll();
    } catch (e) { setError(e.message); }
  };

  const handleUnblock = async (id) => {
    try { await dealerHubApi.unblockDate(userId, id); fetchAll(); }
    catch (e) { setError(e.message); }
  };

  const handleSubmitEndShift = async () => {
    if (!endShiftModal) return;
    try {
      await dealerHubApi.submitEndOfShift(endShiftModal.gameId, userId, {
        playerCount: parseInt(esPlayers) || null,
        cashCount: parseFloat(esCash) || null,
        tipTotal: parseFloat(esTips) || null,
        notes: esNotes,
      });
      setEndShiftModal(null); setEsPlayers(''); setEsCash(''); setEsTips(''); setEsNotes('');
      fetchAll();
    } catch (e) { setError(e.message); }
  };

  const handleReportIncident = async () => {
    try {
      await dealerHubApi.reportIncident(userId, null, incType, incDesc, incSeverity);
      setIncidentModal(false); setIncDesc(''); setIncSeverity('low');
      alert('Incident reported');
    } catch (e) { setError(e.message); }
  };

  const openThread = async (threadId) => {
    setActiveThread(threadId);
    try {
      const r = await dealerHubApi.getThreadMessages(threadId);
      setThreadMessages(r.data?.messages || []);
      await dealerHubApi.markThreadRead(threadId, userId);
      fetchAll();
    } catch (e) { setError(e.message); }
  };

  const sendThreadMessage = async () => {
    if (!threadDraft.trim() || !activeThread) return;
    try {
      await dealerHubApi.postThreadMessage(activeThread, userId, threadDraft);
      setThreadDraft('');
      const r = await dealerHubApi.getThreadMessages(activeThread);
      setThreadMessages(r.data?.messages || []);
    } catch (e) { setError(e.message); }
  };

  const startNewThread = async () => {
    const subject = prompt('Subject of message to admin:');
    if (!subject) return;
    try {
      const r = await dealerHubApi.createThread(userId, subject);
      const id = r.data?.id;
      if (id) { fetchAll(); openThread(id); }
    } catch (e) { setError(e.message); }
  };

  const exportEarningsCSV = () => {
    if (!earnings?.history) return;
    const rows = [['Date', 'Venue', 'Amount']];
    earnings.history.forEach(h => rows.push([new Date(h.earnedAt).toLocaleDateString(), h.venueName || '', h.amount]));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `earnings-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Pull-to-refresh
  const onTouchStart = (e) => { if (window.scrollY === 0) touchStart.current = e.touches[0].clientY; };
  const onTouchMove = (e) => {
    if (touchStart.current && e.touches[0].clientY - touchStart.current > 80 && !refreshing) {
      setRefreshing(true); fetchAll(); touchStart.current = 0;
    }
  };
  const onTouchEnd = () => { touchStart.current = 0; };

  // ===== Computed =====
  const formatDateTime = (d) => new Date(d).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const nextShift = shifts.find(s => new Date(s.startTime) > new Date()) || shifts[0];
  const activeShift = shifts.find(s => s.checkedInAt && new Date(s.startTime).toDateString() === new Date().toDateString());
  const nextCountdown = nextShift ? Math.max(0, Math.floor((new Date(nextShift.startTime) - Date.now()) / 60000)) : null;
  const incomingSwaps = swaps.filter(s => s.status === 'pending' && s.toDealerId === userId).length;

  const fg = darkMode ? '#fff' : '#1a2a3e';
  const bg = darkMode ? '#0d1929' : '#f4f6f8';
  const card = darkMode ? '#1a2a3e' : '#fff';
  const muted = darkMode ? '#aaa' : '#666';
  const border = darkMode ? '#22384e' : '#e5e7eb';

  return (
    <div className="dh-fullscreen" style={{ background: bg, color: fg, minHeight: '100vh' }}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      {/* Top bar */}
      <div className="dh-topbar" style={{ background: card, borderBottom: `1px solid ${border}` }}>
        <div className="dh-topbar-left" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name={username} size={42} frame={stats && computeLevel(stats).tier} />
          <div>
            <h2 style={{ color: fg, margin: 0, fontSize: 18 }}>{getGreeting(username?.split(' ')[0] || 'Dealer')}</h2>
            <div style={{ fontSize: 11, color: muted, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>Level {stats ? computeLevel(stats).level : 1} · {stats ? computeLevel(stats).tier : 'bronze'}</span>
              <span style={{ color: online ? '#16a34a' : '#dc2626' }}>{online ? '● online' : '● offline'}</span>
              {computeStreak(shifts) > 0 && <span><span className="dh-streak-fire">🔥</span> {computeStreak(shifts)} day streak</span>}
            </div>
          </div>
        </div>
        <div className="dh-topbar-right">
          <button className="dh-btn-secondary" onClick={() => setDarkMode(!darkMode)} title="Toggle theme">{darkMode ? '☀️' : '🌙'}</button>
          <button className="dh-btn-secondary" onClick={() => setIncidentModal(true)} title="Report incident">⚠️</button>
          <button className="dh-btn-secondary" onClick={() => setShowPrefs(true)}>⚙️</button>
          <button className="dh-btn-secondary" onClick={onLogout}>Sign Out</button>
        </div>
      </div>

      {/* Today banner */}
      {nextShift && nextCountdown !== null && (
        <div className="dh-hero-gradient" style={{ margin: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>Next Shift</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{nextShift.venue?.name} · {formatDateTime(nextShift.startTime)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>
              {nextCountdown < 60 ? `${nextCountdown}m` : `${Math.floor(nextCountdown/60)}h ${nextCountdown%60}m`}
            </div>
            {nextShift.venue?.address && (
              <a style={{ color: '#fff', fontSize: 12, textDecoration: 'underline' }}
                 href={`https://maps.google.com/?q=${encodeURIComponent(nextShift.venue.address)}`}
                 target="_blank" rel="noreferrer">📍 Directions</a>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="dh-tabs" style={{ background: card, borderBottom: `1px solid ${border}` }}>
        <button className={`dh-tab ${tab === 'shifts' ? 'active' : ''}`} onClick={() => setTab('shifts')}>Shifts ({shifts.length})</button>
        <button className={`dh-tab ${tab === 'open' ? 'active' : ''}`} onClick={() => setTab('open')}>Open ({openShifts.length})</button>
        <button className={`dh-tab ${tab === 'calendar' ? 'active' : ''}`} onClick={() => setTab('calendar')}>Calendar</button>
        <button className={`dh-tab ${tab === 'availability' ? 'active' : ''}`} onClick={() => setTab('availability')}>Off Days</button>
        <button className={`dh-tab ${tab === 'earnings' ? 'active' : ''}`} onClick={() => setTab('earnings')}>Earnings</button>
        <button className={`dh-tab ${tab === 'swaps' ? 'active' : ''}`} onClick={() => setTab('swaps')}>
          Swaps {incomingSwaps > 0 && <span className="dh-badge">{incomingSwaps}</span>}
        </button>
        <button className={`dh-tab ${tab === 'chat' ? 'active' : ''}`} onClick={() => setTab('chat')}>Chat</button>
        <button className={`dh-tab ${tab === 'stats' ? 'active' : ''}`} onClick={() => setTab('stats')}>Stats</button>
        <button className={`dh-tab ${tab === 'inbox' ? 'active' : ''}`} onClick={() => setTab('inbox')}>
          Inbox {unreadCount > 0 && <span className="dh-badge">{unreadCount}</span>}
        </button>
      </div>

      {error && <div className="dh-error" onClick={() => setError(null)}>{error} ✕</div>}
      {refreshing && <div style={{ textAlign: 'center', padding: 8, color: muted }}>↻ Refreshing...</div>}

      <div className="dh-content" style={{ paddingBottom: activeShift ? 80 : 20 }}>
        {loading ? <Skeleton count={4} /> :
        // ===== SHIFTS =====
        tab === 'shifts' ? (
          <div className="dh-shifts">
            <div style={{ padding: '8px 16px' }}>
              <button className="dh-btn-primary" style={{ width: '100%' }} onClick={openSelfCheckin}>
                📍 Check Into Any Game (drop-in)
              </button>
            </div>
            {shifts.length === 0 ? (
              <div className="dh-empty"><div className="dh-empty-icon">📅</div><p>No scheduled shifts — use the button above to check into any game</p></div>
            ) : shifts.map(s => (
              <div key={s.gameId} className="dh-shift-card" style={{ background: card, border: `1px solid ${border}` }}>
                <div className="dh-shift-header">
                  <div className="dh-shift-time" style={{ color: fg }}>{formatDateTime(s.startTime)}</div>
                  <span className={`dh-shift-status status-${s.confirmationStatus}`}>{s.confirmationStatus}</span>
                </div>
                <div className="dh-shift-venue" style={{ color: fg }}>{s.venue.name}</div>
                {s.venue.address && (
                  <a className="dh-shift-address" style={{ color: muted, textDecoration: 'underline' }}
                     href={`https://maps.google.com/?q=${encodeURIComponent(s.venue.address)}`} target="_blank" rel="noreferrer">
                    📍 {s.venue.address}
                  </a>
                )}
                {s.tableNumber && <div className="dh-shift-table">Table {s.tableNumber}</div>}
                <div className="dh-shift-actions">
                  {s.confirmationStatus !== 'accepted' && (
                    <button className="dh-btn-primary" onClick={() => handleConfirm(s.gameId, 'accepted')}>Accept</button>
                  )}
                  {s.confirmationStatus !== 'declined' && s.confirmationStatus !== 'accepted' && (
                    <button className="dh-btn-secondary" onClick={() => handleConfirm(s.gameId, 'declined')}>Decline</button>
                  )}
                  {s.confirmationStatus === 'accepted' && !s.checkedInAt && (
                    <button className="dh-btn-checkin" onClick={() => handleCheckIn(s.gameId)} disabled={checkingIn === s.gameId}>
                      {checkingIn === s.gameId ? 'Checking in...' : '📍 Check In (GPS)'}
                    </button>
                  )}
                  {s.checkedInAt && (
                    <>
                      <span className="dh-checked-in">✓ Checked in ({s.distanceMiles?.toFixed(2)} mi)</span>
                      <button className="dh-btn-primary" style={{ marginTop: 8, width: '100%' }}
                        onClick={() => navigate(`/dealer/game/${s.gameId}${s.tableNumber ? `?table=${s.tableNumber}` : ''}`)}>
                        ▶️ Run Table {s.tableNumber || ''}
                      </button>
                      <button className="dh-btn-secondary" style={{ marginTop: 6, width: '100%' }}
                        onClick={() => setEndShiftModal({ gameId: s.gameId, venue: s.venue.name })}>
                        📋 End-of-Shift Report
                      </button>
                    </>
                  )}
                  {s.confirmationStatus === 'accepted' && (
                    <button className="dh-btn-secondary" style={{ marginTop: 8 }}
                      onClick={() => { setSwapModal({ shift: s }); setSwapTarget(''); setSwapNote(''); }}>
                      🔄 Request Swap
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) :
        // ===== OPEN SHIFTS =====
        tab === 'open' ? (
          <div className="dh-shifts">
            <h3 style={{ color: fg, padding: '0 16px' }}>Open Shifts (claim any)</h3>
            {openShifts.length === 0 ? (
              <div className="dh-empty"><div className="dh-empty-icon">📋</div><p>No open shifts</p></div>
            ) : openShifts.map(o => (
              <div key={o.id} className="dh-shift-card" style={{ background: card, border: `1px solid ${border}` }}>
                <div className="dh-shift-time" style={{ color: fg }}>{formatDateTime(o.start_time)}</div>
                <div className="dh-shift-venue" style={{ color: fg }}>{o.venue_name}</div>
                {o.table_number && <div className="dh-shift-table">Table {o.table_number}</div>}
                <button className="dh-btn-primary" style={{ marginTop: 8, width: '100%' }} onClick={() => handleClaimOpen(o.id)}>
                  ✋ Claim This Shift
                </button>
              </div>
            ))}
          </div>
        ) :
        // ===== CALENDAR =====
        tab === 'calendar' ? (
          <div className="dh-calendar">
            {[0,1,2,3,4,5,6].map(offset => {
              const day = new Date(); day.setDate(day.getDate() + offset);
              const dayShifts = shifts.filter(s => new Date(s.startTime).toDateString() === day.toDateString());
              const blocked = availability.find(a => a.blocked_date === day.toISOString().split('T')[0]);
              return (
                <div key={offset} className="dh-cal-day" style={{ background: card, opacity: blocked ? 0.5 : 1 }}>
                  <div className="dh-cal-date">
                    <strong style={{ color: fg }}>{day.toLocaleDateString('en-US', { weekday: 'short' })}</strong>
                    <span style={{ color: muted }}>{day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className="dh-cal-shifts">
                    {blocked && <span style={{ color: '#dc2626', fontWeight: 600 }}>🚫 Off</span>}
                    {dayShifts.length === 0 && !blocked ? (
                      <span className="dh-cal-empty" style={{ color: muted }}>No shifts</span>
                    ) : dayShifts.map(s => (
                      <div key={s.gameId} style={{ fontSize: 12, color: fg, padding: '4px 8px', borderRadius: 4, background: darkMode ? '#22384e' : '#eef' }}>
                        {new Date(s.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} {s.venue.name}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) :
        // ===== AVAILABILITY =====
        tab === 'availability' ? (
          <div className="dh-shifts">
            <div style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ color: fg, margin: 0 }}>Days I'm Off</h3>
              <button className="dh-btn-primary" onClick={() => setBlockDateModal(true)}>+ Block Date</button>
            </div>
            {availability.length === 0 ? (
              <div className="dh-empty"><div className="dh-empty-icon">🗓️</div><p>No blocked dates</p></div>
            ) : availability.map(a => (
              <div key={a.id} className="dh-shift-card" style={{ background: card, border: `1px solid ${border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, color: fg }}>{a.blocked_date}</div>
                    {a.note && <div style={{ fontSize: 12, color: muted }}>{a.note}</div>}
                  </div>
                  <button className="dh-btn-secondary" onClick={() => handleUnblock(a.id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        ) :
        // ===== EARNINGS =====
        tab === 'earnings' ? (
          <div className="dh-earnings">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, padding: 16 }}>
              <div className="dh-stat-big">
                <div style={{ fontSize: 11, color: muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Tonight</div>
                <div className="dh-big-number"><AnimatedNumber prefix="$" value={earnings?.tonightEarnings || 0} /></div>
              </div>
              <div className="dh-stat-big">
                <div style={{ fontSize: 11, color: muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Earned</div>
                <div className="dh-big-number"><AnimatedNumber prefix="$" value={earnings?.totalEarnings || 0} /></div>
              </div>
              <div className="dh-stat-big">
                <div style={{ fontSize: 11, color: muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Shifts</div>
                <div className="dh-big-number"><AnimatedNumber value={earnings?.totalShifts || 0} /></div>
              </div>
            </div>
            {earnings?.history && earnings.history.length > 1 && (
              <div style={{ padding: 16, background: card, margin: 16, borderRadius: 12 }}>
                <div style={{ fontSize: 12, color: muted, marginBottom: 8 }}>Last {earnings.history.length} shifts</div>
                <Sparkline data={earnings.history.map(h => h.amount).reverse()} width={300} height={60} />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, padding: '0 16px', marginTop: 12 }}>
              <button className="dh-btn-secondary" onClick={() => setTipSplitModal(true)}>🧮 Tip Split Calculator</button>
              <button className="dh-btn-secondary" onClick={exportEarningsCSV}>📥 Export CSV</button>
            </div>
            <h3 style={{ marginTop: 24, marginBottom: 12, padding: '0 16px', color: fg }}>Recent Earnings</h3>
            {(earnings?.history || []).length === 0 ? (
              <p className="dh-empty">No earnings recorded yet</p>
            ) : (
              <div className="dh-history">
                {earnings.history.map((h, i) => (
                  <div key={i} className="dh-history-item" style={{ background: card, border: `1px solid ${border}` }}>
                    <div>
                      <div className="dh-history-venue" style={{ color: fg }}>{h.venueName || 'Game'}</div>
                      <div className="dh-history-date" style={{ color: muted }}>{formatDate(h.earnedAt)}</div>
                    </div>
                    <div className="dh-history-amount">${h.amount?.toFixed?.(2) || h.amount}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) :
        // ===== SWAPS =====
        tab === 'swaps' ? (
          <div className="dh-shifts">
            {swaps.length === 0 ? (
              <div className="dh-empty"><div className="dh-empty-icon">🔄</div><p>No swap requests</p></div>
            ) : swaps.map(sw => {
              const isIncoming = sw.toDealerId === userId;
              return (
                <div key={sw.id} className="dh-shift-card" style={{ background: card, border: `1px solid ${border}` }}>
                  <div className="dh-shift-header">
                    <div className="dh-shift-time" style={{ color: fg }}>{formatDateTime(sw.gameStartTime || sw.startTime)}</div>
                    <span className={`dh-shift-status status-${sw.status}`}>{sw.status}</span>
                  </div>
                  <div className="dh-shift-venue" style={{ color: fg }}>{sw.venueName || sw.venue?.name || 'Game'}</div>
                  <div style={{ fontSize: 13, color: muted, marginTop: 4 }}>
                    {isIncoming ? `From: ${sw.fromDealerName}` : `To: ${sw.toDealerName}`}
                  </div>
                  {sw.note && <div style={{ fontSize: 13, marginTop: 8, fontStyle: 'italic', color: fg }}>"{sw.note}"</div>}
                  {isIncoming && sw.status === 'pending' && (
                    <div className="dh-shift-actions">
                      <button className="dh-btn-primary" onClick={() => handleRespondSwap(sw.id, 'accepted')}>Accept</button>
                      <button className="dh-btn-secondary" onClick={() => handleRespondSwap(sw.id, 'declined')}>Decline</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) :
        // ===== CHAT =====
        tab === 'chat' ? (
          <div className="dh-shifts">
            <div style={{ padding: 16, display: 'flex', justifyContent: 'space-between' }}>
              <h3 style={{ color: fg, margin: 0 }}>Messages</h3>
              <button className="dh-btn-primary" onClick={startNewThread}>+ New</button>
            </div>
            {activeThread ? (
              <div style={{ background: card, margin: 16, borderRadius: 12, padding: 16 }}>
                <button className="dh-btn-secondary" onClick={() => { setActiveThread(null); setThreadMessages([]); }}>← Back</button>
                <div style={{ maxHeight: 400, overflowY: 'auto', marginTop: 12 }}>
                  {threadMessages.map(m => (
                    <div key={m.id} style={{
                      background: m.sender_id === userId ? '#2563eb' : (darkMode ? '#22384e' : '#eef'),
                      color: m.sender_id === userId ? '#fff' : fg,
                      padding: 10, borderRadius: 12, margin: '6px 0', maxWidth: '75%',
                      marginLeft: m.sender_id === userId ? 'auto' : 0,
                    }}>
                      <div>{m.body}</div>
                      <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>{new Date(m.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <input value={threadDraft} onChange={e => setThreadDraft(e.target.value)} placeholder="Type a message..."
                    onKeyDown={e => e.key === 'Enter' && sendThreadMessage()}
                    style={{ flex: 1, padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}` }} />
                  <button className="dh-btn-primary" onClick={sendThreadMessage}>Send</button>
                </div>
              </div>
            ) : threads.length === 0 ? (
              <div className="dh-empty"><div className="dh-empty-icon">💬</div><p>No conversations</p></div>
            ) : threads.map(t => (
              <div key={t.id} className="dh-shift-card" style={{ background: card, border: `1px solid ${border}`, cursor: 'pointer' }} onClick={() => openThread(t.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong style={{ color: fg }}>{t.subject}</strong>
                  {t.unread > 0 && <span className="dh-badge">{t.unread}</span>}
                </div>
                <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>{t.preview}</div>
                <div style={{ fontSize: 11, color: muted }}>{new Date(t.last_message_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        ) :
        // ===== STATS =====
        tab === 'stats' ? (
          <div style={{ padding: 16 }}>
            {!stats ? <Skeleton count={3} /> : (() => {
              const lvl = computeLevel(stats);
              const streak = computeStreak(shifts);
              const achievements = computeAchievements(stats, shifts, streak);
              return (
                <>
                  {/* Level card */}
                  <div className="dh-stat-big" style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 11, color: muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Level {lvl.level}</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: fg, textTransform: 'capitalize' }}>{lvl.tier} Dealer</div>
                      </div>
                      <Avatar name={username} size={56} frame={lvl.tier} />
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                      <div style={{ width: `${lvl.progress}%`, height: 8, background: 'linear-gradient(90deg, #2563eb, #7c3aed)' }}></div>
                    </div>
                    <div style={{ fontSize: 11, color: muted, marginTop: 4 }}>{lvl.xp} XP · {lvl.nextLevelXp - lvl.xp} to next level</div>
                  </div>

                  {/* Streak */}
                  {streak > 0 && (
                    <div className="dh-stat-big" style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: muted, textTransform: 'uppercase' }}>Current Streak</div>
                      <div className="dh-big-number"><span className="dh-streak-fire">🔥</span> <AnimatedNumber value={streak} suffix=" days" /></div>
                    </div>
                  )}

                  {/* Number cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
                    <div className="dh-stat-big">
                      <div style={{ fontSize: 11, color: muted }}>Games (30d)</div>
                      <div className="dh-big-number" style={{ fontSize: 36 }}><AnimatedNumber value={stats.games_30d || 0} /></div>
                    </div>
                    <div className="dh-stat-big">
                      <div style={{ fontSize: 11, color: muted }}>All-time</div>
                      <div className="dh-big-number" style={{ fontSize: 36 }}><AnimatedNumber value={stats.games_total || 0} /></div>
                    </div>
                    <div className="dh-stat-big">
                      <div style={{ fontSize: 11, color: muted }}>Check-ins</div>
                      <div className="dh-big-number" style={{ fontSize: 36 }}><AnimatedNumber value={stats.checkins_total || 0} /></div>
                    </div>
                    <div className="dh-stat-big">
                      <div style={{ fontSize: 11, color: muted }}>Rating</div>
                      <div className="dh-big-number" style={{ fontSize: 36 }}>
                        {stats.rating_count > 0 ? `${Math.round((stats.rating_sum / stats.rating_count + 1) * 50)}%` : 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* Heatmap */}
                  <div style={{ background: card, padding: 16, borderRadius: 12, marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: muted, marginBottom: 8 }}>Last 90 days</div>
                    <ContributionHeatmap shifts={shifts} />
                  </div>

                  {/* Achievements */}
                  <div style={{ background: card, padding: 16, borderRadius: 12 }}>
                    <div style={{ fontSize: 12, color: muted, marginBottom: 8 }}>🏆 Achievements ({achievements.length})</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {achievements.length === 0 ? <span style={{ color: muted, fontSize: 13 }}>Deal your first game to start earning</span> :
                        achievements.map(a => (
                          <span key={a.id} className="dh-badge-trophy" title={a.desc}>{a.icon} {a.name}</span>
                        ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        ) :
        // ===== INBOX =====
        (
          <div className="dh-inbox">
            <div style={{ padding: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button className="dh-btn-secondary" onClick={async () => { await notificationsApi.markAllRead(userId); fetchAll(); }}>✓ Mark all read</button>
              <button className="dh-btn-secondary" onClick={async () => { await notificationsApi.snooze(userId, 1); setError('Snoozed 1h'); }}>😴 Snooze 1h</button>
              <button className="dh-btn-secondary" onClick={async () => { await notificationsApi.snooze(userId, 4); setError('Snoozed 4h'); }}>😴 Snooze 4h</button>
              <button className="dh-btn-secondary" onClick={async () => { await notificationsApi.snooze(userId, 24); setError('Snoozed 24h'); }}>😴 Snooze 24h</button>
            </div>
            {messages.length === 0 ? (
              <div className="dh-empty"><div className="dh-empty-icon">📭</div><p>No messages</p></div>
            ) : messages.map(m => (
              <div key={m.id} className={`dh-message ${!m.readAt ? 'unread' : ''}`} style={{ background: card }}
                onClick={() => !m.readAt && handleMarkRead(m.id)}>
                <div className="dh-message-header">
                  <div className="dh-message-title" style={{ color: fg }}>{m.title}</div>
                  <div className="dh-message-date" style={{ color: muted }}>{formatDate(m.createdAt)}</div>
                </div>
                <div className="dh-message-body" style={{ color: muted }}>{m.body}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sticky active table bar */}
      {activeShift && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: '#16a34a', color: '#fff', padding: '14px 20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          zIndex: 100, boxShadow: '0 -4px 12px rgba(0,0,0,0.3)',
        }} onClick={() => navigate(`/dealer/game/${activeShift.gameId}${activeShift.tableNumber ? `?table=${activeShift.tableNumber}` : ''}`)}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>RUNNING NOW</div>
            <div style={{ fontWeight: 700 }}>🟢 {activeShift.venue.name} {activeShift.tableNumber && `· Table ${activeShift.tableNumber}`}</div>
          </div>
          <div style={{ fontSize: 24 }}>▶️</div>
        </div>
      )}

      {/* ============ MODALS ============ */}

      {/* Self check-in modal */}
      {selfCheckinModal && (
        <Modal onClose={() => setSelfCheckinModal(false)} title="📍 Drop-in Check In" darkMode={darkMode}>
          <p style={{ color: muted, fontSize: 13 }}>Pick a game happening today. You'll be added as dealer + GPS-checked in (must be within 1 mile of venue).</p>
          {todayGames.length === 0 ? (
            <p style={{ textAlign: 'center', padding: 20 }}>No games today</p>
          ) : (
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {todayGames.map(g => (
                <div key={g.id} style={{ background: bg, padding: 12, borderRadius: 8, marginBottom: 8, border: `1px solid ${border}` }}>
                  <div style={{ fontWeight: 700 }}>{g.venue?.name}</div>
                  <div style={{ fontSize: 12, color: muted }}>
                    {new Date(g.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} · {(g.dealers || []).length} dealer{(g.dealers||[]).length !== 1 ? 's' : ''} assigned
                  </div>
                  <button className="dh-btn-primary" style={{ width: '100%', marginTop: 8 }}
                    onClick={() => handleSelfCheckin(g)} disabled={checkingIn === g.id}>
                    {checkingIn === g.id ? 'Checking in...' : '📍 Check In Here'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* Swap modal */}
      {swapModal && (
        <Modal onClose={() => setSwapModal(null)} title="Request Shift Swap" darkMode={darkMode}>
          <p style={{ color: muted, fontSize: 14 }}>{swapModal.shift.venue?.name} — {formatDateTime(swapModal.shift.startTime)}</p>
          <div style={{ marginTop: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>Swap with:</label>
            <select value={swapTarget} onChange={e => setSwapTarget(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}` }}>
              <option value="">Choose a dealer...</option>
              {allDealers.map(d => <option key={d.id} value={d.id}>{d.firstName || d.first_name} {d.lastName || d.last_name}</option>)}
            </select>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>Note:</label>
            <textarea value={swapNote} onChange={e => setSwapNote(e.target.value)} rows={3}
              style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}` }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="dh-btn-secondary" onClick={() => setSwapModal(null)} style={{ flex: 1 }}>Cancel</button>
            <button className="dh-btn-primary" onClick={handleRequestSwap} disabled={!swapTarget} style={{ flex: 1 }}>Send</button>
          </div>
        </Modal>
      )}

      {/* Block date modal */}
      {blockDateModal && (
        <Modal onClose={() => setBlockDateModal(false)} title="Block a Date Off" darkMode={darkMode}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>Date:</label>
          <input type="date" value={blockDate} onChange={e => setBlockDate(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}` }} />
          <label style={{ display: 'block', marginTop: 12, marginBottom: 6, fontSize: 13 }}>Note (optional):</label>
          <input value={blockNote} onChange={e => setBlockNote(e.target.value)} placeholder="Vacation, doctor, etc."
            style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}` }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="dh-btn-secondary" onClick={() => setBlockDateModal(false)} style={{ flex: 1 }}>Cancel</button>
            <button className="dh-btn-primary" onClick={handleBlockDate} disabled={!blockDate} style={{ flex: 1 }}>Block</button>
          </div>
        </Modal>
      )}

      {/* Tip split modal */}
      {tipSplitModal && (
        <Modal onClose={() => setTipSplitModal(false)} title="Tip Split Calculator" darkMode={darkMode}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>Total Tips:</label>
          <input type="number" value={tipAmount} onChange={e => setTipAmount(e.target.value)} placeholder="$0.00"
            style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}` }} />
          <label style={{ display: 'block', marginTop: 12, marginBottom: 6, fontSize: 13 }}># of Dealers:</label>
          <input type="number" value={tipDealers} onChange={e => setTipDealers(parseInt(e.target.value)||1)} min={1}
            style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}` }} />
          <div style={{ marginTop: 16, padding: 16, background: bg, borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: muted }}>Each Dealer Gets:</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#16a34a' }}>
              ${((parseFloat(tipAmount)||0)/tipDealers).toFixed(2)}
            </div>
          </div>
          <button className="dh-btn-secondary" onClick={() => setTipSplitModal(false)} style={{ width: '100%', marginTop: 16 }}>Done</button>
        </Modal>
      )}

      {/* End-of-shift modal */}
      {endShiftModal && (
        <Modal onClose={() => setEndShiftModal(null)} title={`End-of-Shift: ${endShiftModal.venue}`} darkMode={darkMode}>
          <label style={{ fontSize: 13 }}># of Players:</label>
          <input type="number" value={esPlayers} onChange={e => setEsPlayers(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}`, marginBottom: 10 }} />
          <label style={{ fontSize: 13 }}>Cash count ($):</label>
          <input type="number" step="0.01" value={esCash} onChange={e => setEsCash(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}`, marginBottom: 10 }} />
          <label style={{ fontSize: 13 }}>Tips total ($):</label>
          <input type="number" step="0.01" value={esTips} onChange={e => setEsTips(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}`, marginBottom: 10 }} />
          <label style={{ fontSize: 13 }}>Notes / incidents:</label>
          <textarea value={esNotes} onChange={e => setEsNotes(e.target.value)} rows={3}
            style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}` }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="dh-btn-secondary" onClick={() => setEndShiftModal(null)} style={{ flex: 1 }}>Cancel</button>
            <button className="dh-btn-primary" onClick={handleSubmitEndShift} style={{ flex: 1 }}>Submit</button>
          </div>
        </Modal>
      )}

      {/* Incident modal */}
      {incidentModal && (
        <Modal onClose={() => setIncidentModal(false)} title="Report an Incident" darkMode={darkMode}>
          <label style={{ fontSize: 13 }}>Type:</label>
          <select value={incType} onChange={e => setIncType(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}`, marginBottom: 10 }}>
            <option value="player">Player issue</option>
            <option value="equipment">Equipment / supplies</option>
            <option value="venue">Venue issue</option>
            <option value="medical">Medical</option>
            <option value="other">Other</option>
          </select>
          <label style={{ fontSize: 13 }}>Severity:</label>
          <select value={incSeverity} onChange={e => setIncSeverity(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}`, marginBottom: 10 }}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <label style={{ fontSize: 13 }}>Description:</label>
          <textarea value={incDesc} onChange={e => setIncDesc(e.target.value)} rows={4}
            style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}` }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="dh-btn-secondary" onClick={() => setIncidentModal(false)} style={{ flex: 1 }}>Cancel</button>
            <button className="dh-btn-primary" onClick={handleReportIncident} style={{ flex: 1 }}>Submit</button>
          </div>
        </Modal>
      )}

      {/* Prefs modal */}
      {showPrefs && (
        <Modal onClose={() => setShowPrefs(false)} title="Notifications & Settings" darkMode={darkMode}>
          <label style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
            <span>📱 SMS shift reminders</span>
            <input type="checkbox" checked={prefs.sms_shift_reminders} onChange={e => setPrefs({...prefs, sms_shift_reminders: e.target.checked})} />
          </label>
          <label style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
            <span>⚠️ SMS shortage alerts</span>
            <input type="checkbox" checked={prefs.sms_shortage_alerts} onChange={e => setPrefs({...prefs, sms_shortage_alerts: e.target.checked})} />
          </label>
          <label style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
            <span>🔔 Push notifications</span>
            {prefs.push_enabled ? <span style={{ color: '#2ecc71' }}>✓ Enabled</span> :
              <button className="dh-btn-primary" onClick={async () => {
                const perm = await requestNotificationPermission();
                if (perm === 'granted') { setPrefs({...prefs, push_enabled: true}); await dealerShiftsApi.setPrefs(userId, {...prefs, push_enabled: true}); }
              }}>Enable</button>}
          </label>
          <label style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
            <span>🌙 Dark mode</span>
            <input type="checkbox" checked={darkMode} onChange={e => setDarkMode(e.target.checked)} />
          </label>

          {/* Change password */}
          <div style={{ borderTop: '1px solid ' + border, paddingTop: 12, marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>🔑 Change Password</div>
            <input type="password" value={pwdCurrent} onChange={e => setPwdCurrent(e.target.value)} placeholder="Current password"
              style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}`, marginBottom: 8 }} />
            <input type="password" value={pwdNew} onChange={e => setPwdNew(e.target.value)} placeholder="New password (min 4 chars)"
              style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}`, marginBottom: 8 }} />
            <input type="password" value={pwdConfirm} onChange={e => setPwdConfirm(e.target.value)} placeholder="Confirm new password"
              style={{ width: '100%', padding: 10, borderRadius: 8, background: bg, color: fg, border: `1px solid ${border}`, marginBottom: 8 }} />
            {pwdMsg && <div style={{ fontSize: 12, color: pwdMsg.startsWith('✓') ? '#16a34a' : '#dc2626', marginBottom: 8 }}>{pwdMsg}</div>}
            <button className="dh-btn-primary" style={{ width: '100%' }} onClick={async () => {
              setPwdMsg('');
              if (!pwdCurrent || !pwdNew) { setPwdMsg('Fill all fields'); return; }
              if (pwdNew.length < 4) { setPwdMsg('New password must be at least 4 chars'); return; }
              if (pwdNew !== pwdConfirm) { setPwdMsg('Passwords do not match'); return; }
              try {
                await usersApi.changePassword(userId, pwdCurrent, pwdNew);
                setPwdMsg('✓ Password updated');
                setPwdCurrent(''); setPwdNew(''); setPwdConfirm('');
              } catch (e) { setPwdMsg(e.message || 'Failed'); }
            }}>Update Password</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="dh-btn-secondary" onClick={() => setShowPrefs(false)} style={{ flex: 1 }}>Cancel</button>
            <button className="dh-btn-primary" onClick={async () => { await dealerShiftsApi.setPrefs(userId, prefs); setShowPrefs(false); }} style={{ flex: 1 }}>Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title, darkMode }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: darkMode ? '#1a2a3e' : '#fff', borderRadius: 16, padding: 28,
        maxWidth: 480, width: '100%', color: darkMode ? '#fff' : '#1a2a3e',
        maxHeight: '90vh', overflowY: 'auto'
      }}>
        <h2 style={{ marginTop: 0 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

export default DealerHub;
